import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { OpsHeading, OpsState, errorText } from './OperationsUI';
import { toast } from '@/components/ui/sonner';
import { RefreshCw, CreditCard, Ship, Send, Link2, Unlink } from 'lucide-react';

const KIND_LABEL = { RETAIL: 'TCP · Tienda simple', WHOLESALER: 'MiPyMe · Mayorista', GLOBAL_PROVIDER: 'Global Provider · Importador' };
const KINDS = ['RETAIL', 'WHOLESALER', 'GLOBAL_PROVIDER'];

export const PlatformSubscriptionPricing = () => {
  const [data, setData] = useState(null), [loading, setLoading] = useState(true), [error, setError] = useState(''), [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData((await api.get('/platform/subscription-pricing')).data); }
    catch (e) { setError(errorText(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setTier = (kind, field, value) => setData((d) => ({ ...d, tiers: { ...d.tiers, [kind]: { ...d.tiers[kind], [field]: value } } }));
  const sendReminders = async () => {
    setBusy(true);
    try { const { data: r } = await api.post('/platform/subscription-reminders/run'); r.ok ? toast.success(`Recordatorios enviados: ${r.sent} (omitidos: ${r.skipped})`) : toast.error('El correo no está configurado'); }
    catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const tiers = {}; KINDS.forEach((k) => { const t = data.tiers[k] || {}; tiers[k] = { base: Number(t.base) || 0, extra_store: Number(t.extra_store) || 0, extra_wholeseller: Number(t.extra_wholeseller) || 0 }; });
      const { data: saved } = await api.put('/platform/subscription-pricing', { currency: data.currency || 'USD', interval: data.interval || 'month', tax_mode: data.tax_mode || 'diy', note: data.note || '', tiers });
      setData(saved); toast.success('Precios de suscripción actualizados');
    } catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="platform-subscription-pricing-page">
      <OpsHeading title="Precios de suscripción" description="Define el precio mensual por categoría (incluye 1 tienda) y el recargo por cada tienda adicional. Aplica al cobro real con Stripe.">
        <Button variant="outline" data-testid="pricing-reload" onClick={load}><RefreshCw /> Actualizar</Button>
      </OpsHeading>
      <Alert className="mb-5" data-testid="pricing-billing-note"><CreditCard /><AlertDescription>El cobro real se procesa con Stripe. Los cambios se aplican a los próximos pagos; no re-facturan suscripciones ya activas.</AlertDescription></Alert>
      <OpsState loading={loading} error={error} reload={load}>
        {data && <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            {KINDS.map((k) => (
              <section key={k} className="seller-panel" data-testid={`pricing-tier-${k}`}>
                <h3 className="font-semibold mb-3">{KIND_LABEL[k]}</h3>
                <div className="space-y-3">
                  <div><Label className="text-xs" htmlFor={`base-${k}`}>Precio base / mes ({data.currency})</Label><Input id={`base-${k}`} type="number" min="0" step="0.01" data-testid={`pricing-base-${k}`} value={data.tiers[k]?.base ?? ''} onChange={(e) => setTier(k, 'base', e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs" htmlFor={`es-${k}`}>Recargo por tienda adicional / mes</Label><Input id={`es-${k}`} type="number" min="0" step="0.01" data-testid={`pricing-extra-store-${k}`} value={data.tiers[k]?.extra_store ?? ''} onChange={(e) => setTier(k, 'extra_store', e.target.value)} className="mt-1" /></div>
                  {k === 'GLOBAL_PROVIDER' && <div><Label className="text-xs" htmlFor={`ew-${k}`}>Recargo por wholeseller adicional / mes</Label><Input id={`ew-${k}`} type="number" min="0" step="0.01" data-testid={`pricing-extra-wholeseller-${k}`} value={data.tiers[k]?.extra_wholeseller ?? ''} onChange={(e) => setTier(k, 'extra_wholeseller', e.target.value)} className="mt-1" /></div>}
                </div>
              </section>
            ))}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div><Label className="text-xs" htmlFor="pricing-tax">Cobro de impuestos (Stripe)</Label>
              <Select value={data.tax_mode || 'diy'} onValueChange={(v) => setData({ ...data, tax_mode: v })}>
                <SelectTrigger id="pricing-tax" className="mt-1" data-testid="pricing-tax-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="diy" data-testid="pricing-tax-diy">Sin impuestos (Stripe solo procesa el pago)</SelectItem>
                  <SelectItem value="calc_only" data-testid="pricing-tax-calc">Stripe calcula el impuesto en el checkout</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Si eliges “Stripe calcula”, se pide dirección en el checkout y Stripe aplica el impuesto. Debes habilitar Stripe Tax en tu panel de Stripe para producción.</p>
            </div>
            <div><Label className="text-xs" htmlFor="pricing-note">Nota (visible internamente)</Label><Input id="pricing-note" data-testid="pricing-note" value={data.note || ''} onChange={(e) => setData({ ...data, note: e.target.value })} className="mt-1" maxLength={500} /></div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button className="seller-primary" disabled={busy} data-testid="pricing-save" onClick={save}>{busy ? 'Guardando…' : 'Guardar precios'}</Button>
            <Button variant="outline" disabled={busy} data-testid="pricing-send-reminders" onClick={sendReminders}><Send /> Enviar recordatorios de cobro</Button>
          </div>
          <GlobalProviderManager />
        </div>}
      </OpsState>
    </div>
  );
};

const GlobalProviderManager = () => {
  const [gps, setGps] = useState([]), [loading, setLoading] = useState(true), [sel, setSel] = useState(null), [detail, setDetail] = useState(null), [pick, setPick] = useState(''), [busy, setBusy] = useState(false);
  const loadGps = useCallback(async () => {
    setLoading(true);
    try { setGps((await api.get('/platform/global-providers')).data.global_providers || []); }
    catch (e) { toast.error(errorText(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadGps(); }, [loadGps]);
  const openGp = async (gp) => {
    setSel(gp.id); setDetail(null); setPick('');
    try { setDetail((await api.get(`/platform/global-providers/${gp.id}/wholesellers`)).data); }
    catch (e) { toast.error(errorText(e)); }
  };
  const link = async () => {
    if (!pick) return; setBusy(true);
    try { await api.post(`/platform/global-providers/${sel}/wholesellers/${pick}/link`); toast.success('Wholeseller asociado'); await openGp({ id: sel }); loadGps(); }
    catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };
  const unlink = async (wsId) => {
    setBusy(true);
    try { await api.post(`/platform/global-providers/${sel}/wholesellers/${wsId}/unlink`); toast.success('Wholeseller desasociado'); await openGp({ id: sel }); loadGps(); }
    catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };
  return (
    <section className="seller-panel mt-4" data-testid="gp-manager">
      <h3 className="font-semibold flex items-center gap-2 mb-1"><Ship className="h-4 w-4" /> Proveedores globales y wholesellers</h3>
      <p className="text-xs text-muted-foreground mb-3">Asocia wholesellers a cada Global Provider. La suscripción del proveedor incluye 1 wholeseller; cada adicional suma el recargo definido arriba.</p>
      {loading ? <p className="text-sm text-muted-foreground">Cargando…</p> : gps.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="gp-empty">No hay proveedores globales todavía.</p> : (
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-2">
            {gps.map((g) => (
              <button key={g.id} type="button" data-testid={`gp-item-${g.id}`} onClick={() => openGp(g)} className={`w-full text-left rounded-lg border p-2.5 text-sm transition-colors ${sel === g.id ? 'border-brand-orange bg-muted' : 'border-border hover:border-brand-orange'}`}>
                <span className="font-medium">{g.name}</span><Badge variant="outline" className="ml-2" data-testid={`gp-count-${g.id}`}>{g.wholeseller_count} wholeseller(s)</Badge>
              </button>
            ))}
          </div>
          <div className="rounded-lg border border-border p-3" data-testid="gp-detail">
            {!detail ? <p className="text-sm text-muted-foreground">Selecciona un Global Provider para administrar sus wholesellers.</p> : <>
              <p className="text-sm font-medium mb-2">{detail.global_provider?.name}</p>
              <ul className="space-y-1.5 mb-3" data-testid="gp-linked">
                {detail.linked.length ? detail.linked.map((w) => <li key={w.id} className="flex items-center justify-between text-sm"><span>{w.name}</span><Button size="sm" variant="ghost" className="text-destructive" disabled={busy} data-testid={`gp-unlink-${w.id}`} onClick={() => unlink(w.id)}><Unlink className="h-3.5 w-3.5" /> Quitar</Button></li>) : <li className="text-xs text-muted-foreground">Sin wholesellers asociados.</li>}
              </ul>
              <div className="flex items-center gap-2">
                <Select value={pick} onValueChange={setPick}>
                  <SelectTrigger className="flex-1" data-testid="gp-pick"><SelectValue placeholder="Wholeseller disponible" /></SelectTrigger>
                  <SelectContent>{detail.available.length ? detail.available.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>) : <SelectItem value="none" disabled>No hay disponibles</SelectItem>}</SelectContent>
                </Select>
                <Button size="sm" disabled={busy || !pick} data-testid="gp-link" onClick={link}><Link2 className="h-3.5 w-3.5" /> Asociar</Button>
              </div>
            </>}
          </div>
        </div>
      )}
    </section>
  );
};

export default PlatformSubscriptionPricing;
